// ---------------------------------------------------------------------------
// PRODUCTION SHADOW — what actually happened in a real search, with REAL
// FeatureSummary data. Recording only: nothing here filters, scores, reorders
// or influences a single card the user sees.
//
// Morgan, Aug 22: "build the production shadow hook using real FeatureSummary
// data... Keep the full attrition trace in that production shadow run, and
// behind the debug flag also score rejected candidates using their real
// measurements."
//
// TURNING IT ON
//   ?shadow=1 on any app URL (persists), or in the console:
//     localStorage.setItem("pleasant.shadow.v1", "1")
//   Off again:
//     localStorage.removeItem("pleasant.shadow.v1")
//
//   With the flag OFF this module costs one localStorage read per search and
//   nothing else — attrition stays disabled, no geometry is measured twice, no
//   route is retained.
//
// THE THREE FAILURE MODES (Morgan's words, and what each one means here)
//
//   1. GATE LOSS      a rejected candidate would have beaten the shown route
//                     using real features. Only counted when the rejected
//                     route is ALSO within the Pleasant time budget — a route
//                     70 minutes longer was never eligible, so its absence
//                     cannot be blamed on the gate. (Morgan, Aug 22, on the
//                     Smokies case I got wrong.)
//
//   2. FLOOR COLLAPSE materially different raw scores arriving at the SAME
//                     post-mutation score. Not a flipped winner — worse: the
//                     score stops distinguishing the routes at all and
//                     something downstream decides instead.
//
//   3. RANKING LOSS   a better-measured candidate SURVIVED to ranking, was
//                     time-eligible, and still lost. No gate involved: this is
//                     the scoring rules choosing badly on their own.
//
// "Better-measured" is defined narrowly and stated on every row, because a
// loose definition would manufacture findings: strictly less highway exposure
// AND a strictly higher scenic component than the route that won.
// ---------------------------------------------------------------------------

import type { ScoredRoute, ClassifiedRoute, FeatureSummary } from "@/utils/scoring";
import { rankScoreFallbackCount, selectionForCard, pleasantSelections,
         clearPleasantSelections } from "@/utils/scoring";
import { analyseCandidate, foldSearch, emptyCounters, formatCandidate,
         formatCounters, type Breakdown, type CandidateShadow,
         type ShadowCounters } from "@/utils/shadowScore";
import { type AttritionTrace, formatTrace, roadsOfSteps } from "@/utils/attrition";

const FLAG_KEY = "pleasant.shadow.v1";

/** Off unless explicitly turned on. Every accessor is guarded: a private
 *  window, a browser blocking site data, or SSR must not throw here. */
export function shadowEnabled(): boolean {
  try {
    if (typeof window !== "undefined") {
      const q = new URLSearchParams(window.location.search);
      if (q.get("shadow") === "1") {
        try { window.localStorage.setItem(FLAG_KEY, "1"); } catch { /* ignore */ }
        return true;
      }
      if (q.get("shadow") === "0") {
        try { window.localStorage.removeItem(FLAG_KEY); } catch { /* ignore */ }
        return false;
      }
    }
    return typeof localStorage !== "undefined" && localStorage.getItem(FLAG_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * REACHABLE FROM THE CONSOLE, or the roll-up may as well not exist.
 *
 * The per-search block prints itself, but the by-band summary - the thing that
 * answers "where is this going wrong" - is a module export nobody can call
 * from devtools. Attached only while the flag is on, so a normal session gets
 * no new global.
 *
 *   __shadow.session()   the by-band roll-up across every search so far
 *   __shadow.runs()      the raw records, if you want to poke at them
 *   __shadow.clear()     start counting again
 */
function exposeOnWindow(): void {
  try {
    if (typeof window === "undefined") return;
    (window as unknown as Record<string, unknown>).__shadow = {
      session: () => { console.log(formatShadowSession()); },
      runs: shadowRuns,
      clear: clearShadow,
    };
  } catch { /* a locked-down window must not break a search */ }
}

const MI = (km: number) => `${(km * 0.621371).toFixed(1)}mi`;
const M_TO_MI = (m: number) => `${(m / 1609.34).toFixed(1)}mi`;

/** Same six bands as the offline sweep, so the two can be compared. */
export type Band = "xs" | "s" | "m" | "l" | "xl" | "xxl";
export function bandOf(km: number): Band {
  if (km < 15) return "xs";
  if (km < 40) return "s";
  if (km < 80) return "m";
  if (km < 160) return "l";
  if (km < 400) return "xl";
  return "xxl";
}
const BAND_LABEL: Record<Band, string> = {
  xs: "under 9 mi", s: "9-25 mi", m: "25-50 mi",
  l: "50-100 mi", xl: "100-250 mi", xxl: "250+ mi",
};

/** One flagged row. Every field is here so a row can be judged by reading it
 *  rather than by trusting the label on it. */
export interface Finding {
  mode: "gate-loss" | "floor-collapse" | "ranking-loss";
  corridor: string;
  band: Band;
  /** For gate-loss: the gate that removed it. Empty for the others. */
  rule?: string;
  measured?: number;
  threshold?: number;
  lostRoads: string;
  lostScore: number;
  lostMin: number;
  lostFeatures: string;
  shownRoads: string;
  shownScore: number;
  shownMin: number;
  shownFeatures: string;
  margin: number;
  /** Set when the losing route's time-eligibility could not be established;
   *  such a row is REPORTED but never COUNTED as a failure. */
  eligibilityUnknown?: boolean;
  note?: string;
}

export interface ShadowRun {
  corridor: string;
  band: Band;
  km: number;
  mode: string;
  generated: number;
  reachedRanking: number;
  pleasant: string | null;
  scenic: string | null;
  findings: Finding[];
}

/** Session store — production runs one search at a time, and the by-band
 *  breakdown only means anything across several. Cleared on reload. */
const RUNS: ShadowRun[] = [];
const COUNTERS: ShadowCounters = emptyCounters();
export function shadowRuns(): ShadowRun[] { return RUNS; }
export function shadowCounters(): ShadowCounters { return COUNTERS; }
export function clearShadow(): void { RUNS.length = 0; }

/** The levers worth reading on a row, in miles and feet. */
function featureLine(f: FeatureSummary | undefined, s: ScoredRoute["scores"] | undefined): string {
  if (!f) return "(no features)";
  const bits: string[] = [
    `hwy ${f.highwayPct.toFixed(0)}%`,
    `main ${(f.majorRoadPct ?? 0).toFixed(0)}%`,
    `resid ${(f.residentialPct ?? 0).toFixed(0)}%`,
  ];
  if (f.fourLaneKm) bits.push(`4-lane ${MI(f.fourLaneKm)}`);
  if (f.fastArterialKm) bits.push(`fast-arterial ${MI(f.fastArterialKm)}`);
  if (f.slowStreetKm) bits.push(`slow-street ${MI(f.slowStreetKm)}`);
  if (f.scenicBywayKm) bits.push(`byway ${MI(f.scenicBywayKm)}`);
  if (f.treeLinedKm) bits.push(`tree-lined ${MI(f.treeLinedKm)}`);
  if (f.parks) bits.push(`parks ${f.parks}`);
  if (f.waterAdjacentMeters) bits.push(`water-adjacent ${M_TO_MI(f.waterAdjacentMeters)}`);
  if (f.carFreeKm) bits.push(`car-free ${MI(f.carFreeKm)}`);
  const cov = f.scenicLayer?.coveredFraction;
  bits.push(cov === undefined ? `scenery-layer NOT LOOKED UP` : `scenery-cover ${(cov * 100).toFixed(0)}%`);
  if (s) bits.push(`| scenic ${s.scenic.toFixed(0)} calm ${s.calm.toFixed(0)} com ${s.community.toFixed(0)} prefMatch ${s.preferenceMatch.toFixed(0)}`);
  return bits.join("  ");
}

/** Strictly less highway AND strictly more scenic. Deliberately narrow. */
function betterMeasured(a: ScoredRoute, b: ScoredRoute): boolean {
  const af = a.features, bf = b.features;
  if (!af || !bf) return false;
  return af.highwayPct < bf.highwayPct - 0.5 && a.scores.scenic > b.scores.scenic + 0.5;
}

export interface ShadowInput {
  corridor: string;
  km: number;
  mode: string;
  /** The enriched, real-feature pool handed to classifyThreeCards. */
  enriched: ScoredRoute[];
  classified: ClassifiedRoute[];
  trace: AttritionTrace | null;
  /** Absolute Pleasant time ceiling in seconds, exactly as production computed
   *  it and passed it to classifyThreeCards. When undefined, time-eligibility
   *  is UNKNOWN and no gate-loss or ranking-loss row is COUNTED — because the
   *  alternative is to re-derive production's budget here, and a
   *  reimplementation that drifts is worse than an unanswered question. */
  pleasantTimeCapSec?: number;
}

export function runProductionShadow(input: ShadowInput): ShadowRun {
  const { corridor, km, mode, enriched, classified, trace, pleasantTimeCapSec } = input;
  const band = bandOf(km);
  const pleasantCard = classified.find((c) => c.classification === "pleasant");
  const scenicCard = classified.find((c) => c.classification === "scenic");
  const shown = pleasantCard ?? classified[0];
  const findings: Finding[] = [];

  const out: string[] = [];
  out.push(``, "=".repeat(78));
  out.push(`SHADOW  ${corridor}  [${band}: ${BAND_LABEL[band]}]  ${MI(km)}  ${mode}`);
  out.push(`  ${enriched.length} candidates entered ranking-side classification`);
  if (pleasantTimeCapSec === undefined)
    out.push(`  ⚠ Pleasant time cap NOT SUPPLIED — time-eligibility unknown, so no`);
  if (pleasantTimeCapSec === undefined)
    out.push(`    gate-loss or ranking-loss row below is COUNTED, only reported.`);

  // Which candidates a gate removed. Hoisted above the candidate loop (it used
  // to be computed only for the summary) so each line can say whether the route
  // was still PRESENT at the Pleasant winner-selection step. Ordering must only
  // ever be judged against candidates that actually reached that step —
  // comparing against something coherence or dedupe already deleted would blame
  // ranking for an upstream rejection.
  const rejectedRoutes = new Set<unknown>();
  for (const r of trace?.rejections ?? []) {
    const c = r.candidate as ScoredRoute | undefined;
    if (c) rejectedRoutes.add(c.route);
  }
  const isEligible = (c: ScoredRoute): boolean | undefined =>
    pleasantTimeCapSec !== undefined ? c.route.duration <= pleasantTimeCapSec : undefined;
  const preFloorOf = (c: ScoredRoute): number | undefined => {
    const v = (c.scores as { pleasantPreFloor?: number }).pleasantPreFloor;
    return Number.isFinite(v) ? v : undefined;
  };

  // ---- every candidate, with the measurements the decision was made from ----
  out.push(``, `  CANDIDATES (real features, not proxies)`);
  const pool: Array<{ id: string; shadow: CandidateShadow }> = [];
  for (const c of enriched) {
    const id = roadsOfSteps(c.route.steps);
    const b = (c as unknown as { breakdown?: Breakdown }).breakdown;
    const pf = preFloorOf(c);
    const el = isEligible(c);
    const inPool = !rejectedRoutes.has(c.route);
    out.push(`    ${c.scores.pleasant.toFixed(1).padStart(6)}  ${(c.route.duration / 60).toFixed(0)}min  ${id}`);
    // RANKING vs DISPLAY, side by side (Morgan, Aug 23). `rank` is what orders
    // candidates; `display` is what the badge shows. `eligible` is the Pleasant
    // time cap; `inPool` says the route was still present at winner selection.
    out.push(
      `            rank ${pf === undefined ? "  (none)" : pf.toFixed(1).padStart(7)}` +
      `   display ${c.scores.pleasant.toFixed(1).padStart(5)}` +
      `   eligible ${el === undefined ? "unknown" : el ? "yes" : "no "}` +
      `   inPool ${inPool ? "yes" : "no "}`,
    );
    out.push(`            ${featureLine(c.features, c.scores)}`);
    if (b) {
      const shadow = analyseCandidate(b);
      pool.push({ id, shadow });
      out.push(formatCandidate(`  ↳ mutation trail`, shadow));
    }
  }
  const w = foldSearch(COUNTERS, pool);
  if (w.changed)
    out.push(``, `  ⚠ POST-SCORE MUTATIONS CHANGED THE WINNER`,
      `      continuous model would pick: ${w.rawWinner}`,
      `      after floors/caps/lifts:     ${w.postWinner}`);

  // ---- DID componentFloor CHANGE THE ORDERING? ------------------------------
  //
  // Mirrors the selector's OWN record rather than rebuilding its comparison.
  // The Pleasant sort has three keys: runsScenicParkway (dominant), then
  // effective (= rankScore - directnessPenalty), then highwayPct ascending. A
  // parkway route can legitimately win with a lower effective value, so the
  // verdict honours that pre-key instead of reporting a false failure.
  //
  // Judged ONLY against the contender set the selector was actually allowed to
  // choose from — anything coherence, dedupe or the time cap removed was never
  // ranking's to pick, and counting it would blame ranking for an upstream
  // rejection. Recording only.
  out.push(``, `  ORDERING VERDICT`);
  const fallbacks = rankScoreFallbackCount();
  const allSel = pleasantSelections();
  // IDENTITY, NOT RECENCY. The card carries the seq of the pool it was chosen
  // from, so this is the selection that produced the RENDERED result — not
  // whichever invocation happened to run last. classifyThreeCards runs three
  // times and re-classifies as enrichment lands, so "last" was never safe.
  const sel = pleasantCard ? selectionForCard(pleasantCard) : undefined;
  out.push(
    `    ${allSel.length} Pleasant selection(s) this search: ` +
    (allSel.map((x) => `#${x.seq} ${x.pass} (${x.poolSize})`).join(", ") || "none"),
  );
  if (!pleasantCard) {
    out.push(`    no Pleasant card was selected — nothing to verify`);
  } else if (!sel) {
    out.push(
      `    the shown Pleasant card's route object is not in any recorded`,
      `    contender set. Matched by route-object identity, which survives the`,
      `    { ...s, classification } re-wrap. Either a selection went unrecorded`,
      `    or something downstream of the selector placed this card.`,
      `      shown: ${roadsOfSteps(pleasantCard.route.steps)}`,
      `      rankScore fallbacks       : ${fallbacks}`,
    );
  } else if (sel.contenders.length < 2) {
    out.push(
      `    selection #${sel.seq} (${sel.pass}) had only ${sel.contenders.length} contender —`,
      `    cannot verify ordering on this corridor (nothing to order).`,
      `      rankScore fallbacks       : ${fallbacks}`,
    );
  } else {
    out.push(`    FINAL PASS = selection #${sel.seq} (${sel.pass}), ${sel.contenders.length} contenders:`);
    for (const c of sel.contenders) {
      out.push(
        `      ${c.effective.toFixed(1).padStart(7)} eff` +
        `   preFloor ${c.preFloor === undefined ? "(none)" : c.preFloor.toFixed(1).padStart(6)}` +
        `   display ${c.display.toFixed(0).padStart(3)}` +
        `   directness -${c.directness.toFixed(1).padStart(5)}` +
        `   ${c.scenicParkway ? "PARKWAY" : "       "}` +
        `   hwy ${String(Math.round(c.highwayPct)).padStart(3)}%   ${c.durationMin}min`,
        `        ${c.roads}`,
      );
    }
    const chosen = roadsOfSteps(pleasantCard.route.steps);
    const anyParkway = sel.contenders.some((c) => c.scenicParkway);
    const field = anyParkway ? sel.contenders.filter((c) => c.scenicParkway) : sel.contenders;
    const best = field.reduce((a2, b2) => (b2.effective > a2.effective ? b2 : a2));
    // Identity, not road-name string equality: two distinct candidates can share
    // a road list, and a string match would silently conflate them.
    const mine = sel.contenders.find((c) => c.route === pleasantCard.route);
    const topMatches = best.route === pleasantCard.route;
    const ok = topMatches && !!mine;
    out.push(
      ``,
      `    shown Pleasant card IS the selector's winning contender: ${topMatches ? "YES" : "NO"}`,
      `      (matched by route-object identity)`,
      `      pass / sequence           : #${sel.seq} ${sel.pass}  (card stamp matches this selection)`,
      `      selector's top pick       : ${sel.top ?? "(none)"}`,
      `      shown Pleasant card       : ${chosen}`,
      `      shown effective           : ${mine === undefined ? "(not in this contender set)" : mine.effective.toFixed(1)}`,
      `      shown pleasantPreFloor    : ${mine?.preFloor === undefined ? "(none)" : mine.preFloor.toFixed(1)}`,
      `      shown display (floored)   : ${mine === undefined ? "n/a" : mine.display.toFixed(0)}`,
      `      highest effective allowed : ${best.effective.toFixed(1)}`,
      `      difference                : ${mine === undefined ? "n/a" : (best.effective - mine.effective).toFixed(1)}`,
      `      parkway pre-key active    : ${anyParkway ? "yes — only parkway routes could win" : "no"}`,
      `      contenders                : ${sel.contenders.length} of ${enriched.length} generated`,
      `      rankScore fallbacks       : ${fallbacks}`,
      `      VERDICT                   : ${ok ? "ordering follows the pre-floor ranking value" : "DIVERGENCE — trace the downstream override"}`,
    );
  }

  // ---- attrition, in full ----
  if (trace) out.push(formatTrace(trace));

  // ---- 1. GATE LOSS ----
  if (trace && shown) {
    const seen = new Set<unknown>();
    for (const r of trace.rejections) {
      if (r.stage === "dedupe") continue;           // a duplicate is not a deletion
      const cand = r.candidate as ScoredRoute | undefined;
      if (!cand?.scores || seen.has(cand.route)) continue;
      seen.add(cand.route);
      const margin = cand.scores.pleasant - shown.scores.pleasant;
      if (margin <= 0) continue;
      const eligible = pleasantTimeCapSec !== undefined
        ? cand.route.duration <= pleasantTimeCapSec
        : undefined;
      if (eligible === false) continue;             // never eligible — not the gate's doing
      findings.push({
        mode: "gate-loss", corridor, band,
        rule: `${r.stage}/${r.rule}`, measured: r.measured, threshold: r.threshold,
        lostRoads: r.roads, lostScore: cand.scores.pleasant,
        lostMin: cand.route.duration / 60, lostFeatures: featureLine(cand.features, cand.scores),
        shownRoads: roadsOfSteps(shown.route.steps), shownScore: shown.scores.pleasant,
        shownMin: shown.route.duration / 60, shownFeatures: featureLine(shown.features, shown.scores),
        margin,
        eligibilityUnknown: eligible === undefined,
        note: eligible === undefined
          ? "time-eligibility unknown — reported, not counted"
          : `within the Pleasant time cap (${(pleasantTimeCapSec! / 60).toFixed(0)} min)`,
      });
    }
  }

  // ---- 2. FLOOR COLLAPSE ----
  {
    const byPost = new Map<number, Array<{ id: string; raw: number }>>();
    for (const { id, shadow } of pool) {
      const k = +shadow.postMutationRanking.toFixed(1);
      byPost.set(k, [...(byPost.get(k) ?? []), { id, raw: shadow.rawRanking }]);
    }
    for (const [post, members] of byPost) {
      if (members.length < 2) continue;
      const raws = members.map((m) => m.raw);
      const spread = Math.max(...raws) - Math.min(...raws);
      if (spread <= 1) continue;
      const hi = members.reduce((a, b) => (b.raw > a.raw ? b : a));
      const lo = members.reduce((a, b) => (b.raw < a.raw ? b : a));
      findings.push({
        mode: "floor-collapse", corridor, band,
        lostRoads: lo.id, lostScore: post, lostMin: 0, lostFeatures: `raw ${lo.raw.toFixed(1)}`,
        shownRoads: hi.id, shownScore: post, shownMin: 0, shownFeatures: `raw ${hi.raw.toFixed(1)}`,
        margin: spread,
        note: `${members.length} candidates, raw spread ${spread.toFixed(1)} pts, all post-mutation ${post.toFixed(1)}`,
      });
    }
  }

  // ---- 3. RANKING LOSS ----
  if (shown) {
    for (const c of enriched) {
      if (c.route === shown.route) continue;
      if (!betterMeasured(c, shown)) continue;
      const survived = classified.some((x) => x.route === c.route)
        || !trace?.rejections.some((r) => (r.candidate as ScoredRoute | undefined)?.route === c.route);
      if (!survived) continue;                      // that is gate loss, already counted
      const eligible = pleasantTimeCapSec !== undefined
        ? c.route.duration <= pleasantTimeCapSec
        : undefined;
      if (eligible === false) continue;
      findings.push({
        mode: "ranking-loss", corridor, band,
        lostRoads: roadsOfSteps(c.route.steps), lostScore: c.scores.pleasant,
        lostMin: c.route.duration / 60, lostFeatures: featureLine(c.features, c.scores),
        shownRoads: roadsOfSteps(shown.route.steps), shownScore: shown.scores.pleasant,
        shownMin: shown.route.duration / 60, shownFeatures: featureLine(shown.features, shown.scores),
        margin: shown.scores.pleasant - c.scores.pleasant,
        eligibilityUnknown: eligible === undefined,
        note: "survived every gate, less highway AND more scenic than the winner, still lost",
      });
    }
  }

  if (findings.length) {
    out.push(``, `  FLAGGED`);
    for (const f of findings) out.push(formatFinding(f));
  } else {
    out.push(``, `  FLAGGED  none`);
  }

  const run: ShadowRun = {
    corridor, band, km, mode,
    generated: trace?.generated ?? enriched.length,
    // classifyThreeCards runs classifyRoutes THREE times (direct / pleasant /
    // scenic), so stages and rejections are recorded three times over. Counting
    // survivors as "the last stage's leaving" would report the scenic pass
    // alone. Distinct routes rejected in ANY pass is the honest figure.
    reachedRanking: enriched.length - rejectedRoutes.size,
    pleasant: pleasantCard ? roadsOfSteps(pleasantCard.route.steps) : null,
    scenic: scenicCard ? roadsOfSteps(scenicCard.route.steps) : null,
    findings,
  };
  RUNS.push(run);
  exposeOnWindow();
  // Reset for the NEXT search. Selections are per-search; letting them
  // accumulate would let a later search match a card against an earlier
  // search's pool by a recycled seq.
  clearPleasantSelections();
  out.push(``, `  → Pleasant: ${run.pleasant ?? "(none)"}`);
  out.push(`  → Scenic:   ${run.scenic ?? "(none)"}`);
  out.push(``, `  ${RUNS.length} shadow runs this session — run  __shadow.session()  for the roll-up`);
  console.log(out.join("\n"));
  return run;
}

function formatFinding(f: Finding): string {
  const head = f.mode === "floor-collapse"
    ? `    [FLOOR COLLAPSE]  ${f.note}`
    : `    [${f.mode === "gate-loss" ? "GATE LOSS" : "RANKING LOSS"}]` +
      (f.rule ? `  ${f.rule}` : ``) +
      (f.measured !== undefined ? `  measured ${f.measured.toFixed(4)}` : ``) +
      (f.threshold !== undefined ? ` vs ${f.threshold}` : ``) +
      (f.eligibilityUnknown ? `   (NOT COUNTED — ${f.note})` : ``);
  if (f.mode === "floor-collapse")
    return [head, `        ${f.shownFeatures}  ${f.shownRoads}`,
                  `        ${f.lostFeatures}  ${f.lostRoads}`].join("\n");
  return [
    head,
    `        NOT SHOWN  ${f.lostScore.toFixed(1)}  ${f.lostMin.toFixed(0)}min  ${f.lostRoads}`,
    `                   ${f.lostFeatures}`,
    `        SHOWN      ${f.shownScore.toFixed(1)}  ${f.shownMin.toFixed(0)}min  ${f.shownRoads}`,
    `                   ${f.shownFeatures}`,
  ].join("\n");
}

/** Roll-up across every search this session, broken out by trip length —
 *  which is where the offline sweep put the problem. */
export function formatShadowSession(): string {
  if (!RUNS.length) return "(no shadow runs yet — turn the flag on and run a search)";
  const out: string[] = ["", "=".repeat(78), `SHADOW SESSION — ${RUNS.length} searches`];

  const bands: Band[] = ["xs", "s", "m", "l", "xl", "xxl"];
  out.push(``, `  band   label        searches  generated  reached  survival  gate  floor  rank`);
  for (const b of bands) {
    const rs = RUNS.filter((r) => r.band === b);
    if (!rs.length) continue;
    const gen = rs.reduce((a, r) => a + r.generated, 0);
    const got = rs.reduce((a, r) => a + r.reachedRanking, 0);
    const count = (m: Finding["mode"]) =>
      rs.reduce((a, r) => a + r.findings.filter((f) => f.mode === m && !f.eligibilityUnknown).length, 0);
    out.push(
      `  ${b.padEnd(6)} ${BAND_LABEL[b].padEnd(12)} ${String(rs.length).padStart(8)} ` +
      `${String(gen).padStart(10)} ${String(got).padStart(8)} ` +
      `${((100 * got) / (gen || 1)).toFixed(0).padStart(7)}% ` +
      `${String(count("gate-loss")).padStart(5)} ${String(count("floor-collapse")).padStart(6)} ` +
      `${String(count("ranking-loss")).padStart(5)}`,
    );
  }

  const all = RUNS.flatMap((r) => r.findings);
  const counted = all.filter((f) => !f.eligibilityUnknown);
  const uncounted = all.length - counted.length;
  out.push(``, `  ${counted.length} counted findings` +
    (uncounted ? `  (+${uncounted} reported but NOT counted — time-eligibility unknown)` : ``));

  for (const m of ["gate-loss", "floor-collapse", "ranking-loss"] as const) {
    const rows = counted.filter((f) => f.mode === m).sort((a, b) => b.margin - a.margin);
    if (!rows.length) continue;
    out.push(``, `  ${m.toUpperCase()}  ${rows.length}`);
    for (const f of rows.slice(0, 12)) out.push(formatFinding(f));
    if (rows.length > 12) out.push(`    ...and ${rows.length - 12} more (shadowRuns() has all of them)`);
  }

  out.push(``, formatCounters(COUNTERS));
  out.push(``, `A flagged row is a QUESTION, not a verdict — read the roads and the`);
  out.push(`measurements before concluding a layer is at fault. Nothing here changed`);
  out.push(`what the user saw.`);
  return out.join("\n");
}
